import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { 
  TrendingUp, TrendingDown, BarChart3, PieChart, Activity, Target, Zap, Users, 
  DollarSign, Award, AlertTriangle, AlertCircle, CheckCircle, Clock, Calendar, ArrowUpRight, 
  ArrowDownRight, Filter, Download, RefreshCw, Eye, Layers, Building2, Globe,
  Factory, Briefcase, Package, Heart, Cpu, Droplet, Wind, Leaf, Shield,
  FileText, Map, Thermometer, Brain
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, Scatter, ScatterChart, ZAxis
} from 'recharts';

const AdvancedAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [usingRealData, setUsingRealData] = useState(false);
  
  // Configuration States
  const [selectedIndustry, setSelectedIndustry] = useState('manufacturing');
  const [companySize, setCompanySize] = useState('mid-cap');
  const [market, setMarket] = useState('eu');
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [timeRange, setTimeRange] = useState('12m');

  // Fetch analytics data from uploads
  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    const data = await apiFetch(`${API}/data/analytics-summary`);
    if (data?._error) {
      console.error('Failed to fetch analytics data:', data.message);
      setUsingRealData(false);
      setLoading(false);
      return;
    }
    setAnalyticsData(data);
    setUsingRealData(data.total_uploads > 0);
    setLoading(false);
  };

  // Industry Configurations
  const industries = [
    { 
      id: 'manufacturing', 
      name: 'Manufacturing', 
      icon: Factory,
      materialTopics: ['E1 Climate', 'E3 Water', 'E4 Biodiversity', 'S1 Workforce', 'S2 Supply Chain'],
      kpis: {
        environmental: 75,
        social: 82,
        governance: 88,
        financial: 85
      }
    },
    { 
      id: 'financial', 
      name: 'Financial Services', 
      icon: DollarSign,
      materialTopics: ['E1 Financed Emissions', 'G1 Governance', 'S4 Customers'],
      kpis: {
        environmental: 68,
        social: 85,
        governance: 92,
        financial: 90
      }
    },
    { 
      id: 'technology', 
      name: 'Technology', 
      icon: Cpu,
      materialTopics: ['E1 Data Centers', 'E5 Circular Economy', 'S1 Labor', 'G1 Ethics'],
      kpis: {
        environmental: 72,
        social: 88,
        governance: 90,
        financial: 92
      }
    },
    { 
      id: 'retail', 
      name: 'Retail', 
      icon: Package,
      materialTopics: ['E1 Supply Chain', 'E2 Pollution', 'S2 Workers', 'S4 Consumers'],
      kpis: {
        environmental: 70,
        social: 80,
        governance: 85,
        financial: 82
      }
    },
    { 
      id: 'energy', 
      name: 'Energy & Utilities', 
      icon: Zap,
      materialTopics: ['E1 Climate', 'E2 Pollution', 'E3 Water', 'S3 Communities'],
      kpis: {
        environmental: 65,
        social: 78,
        governance: 88,
        financial: 88
      }
    },
    { 
      id: 'healthcare', 
      name: 'Healthcare', 
      icon: Heart,
      materialTopics: ['S1 Labor', 'S4 Patient Safety', 'E2 Waste', 'G1 Ethics'],
      kpis: {
        environmental: 73,
        social: 90,
        governance: 92,
        financial: 86
      }
    }
  ];

  // Company Size Configurations
  const companySizes = [
    { 
      id: 'sme', 
      name: 'SME', 
      description: '< 250 employees',
      esrsRequirements: 'Simplified disclosure',
      materialTopics: 5,
      valueChainTiers: 1
    },
    { 
      id: 'mid-cap', 
      name: 'Mid-Cap', 
      description: '250-2,000 employees',
      esrsRequirements: 'Full disclosure (phased)',
      materialTopics: 15,
      valueChainTiers: 2
    },
    { 
      id: 'large-cap', 
      name: 'Large-Cap', 
      description: '2,000+ employees',
      esrsRequirements: 'Full mandatory disclosure',
      materialTopics: 20,
      valueChainTiers: 3
    }
  ];

  // Market Configurations
  const markets = [
    { 
      id: 'eu', 
      name: 'EU Market', 
      frameworks: ['ESRS', 'CSRD', 'EU Taxonomy'],
      mandatory: true
    },
    { 
      id: 'global', 
      name: 'Global', 
      frameworks: ['ESRS', 'GRI', 'SASB', 'TCFD'],
      mandatory: false
    },
    { 
      id: 'emerging', 
      name: 'Emerging Markets', 
      frameworks: ['SDGs', 'Basic ESG'],
      mandatory: false
    }
  ];

  // Report Types
  const reportTypes = [
    {
      id: 'executive',
      name: 'Executive ESG Summary',
      description: 'Overall ESG score, highlights, risks - Investor-ready format',
      icon: Award,
      color: 'purple',
      bgClass: 'bg-purple-100',
      textClass: 'text-purple-600',
      btnClass: 'bg-purple-600 hover:bg-purple-700',
      pages: '2-3 pages'
    },
    {
      id: 'esrs-compliance',
      name: 'ESRS Compliance Report',
      description: 'All mandatory disclosures, materiality, IRO summary, metrics',
      icon: FileText,
      color: 'blue',
      bgClass: 'bg-blue-100',
      textClass: 'text-blue-600',
      btnClass: 'bg-blue-600 hover:bg-blue-700',
      pages: '15-20 pages'
    },
    {
      id: 'benchmark',
      name: 'Industry Benchmark Report',
      description: 'Performance vs peers, best-in-class, competitive positioning',
      icon: TrendingUp,
      color: 'green',
      bgClass: 'bg-green-100',
      textClass: 'text-green-600',
      btnClass: 'bg-green-600 hover:bg-green-700',
      pages: '5-7 pages'
    },
    {
      id: 'value-chain',
      name: 'Value Chain Risk Report',
      description: 'Supplier risk mapping, geographic heatmap, audit findings',
      icon: Map,
      color: 'orange',
      bgClass: 'bg-orange-100',
      textClass: 'text-orange-600',
      btnClass: 'bg-orange-600 hover:bg-orange-700',
      pages: '8-10 pages'
    },
    {
      id: 'climate-transition',
      name: 'Climate Transition Plan',
      description: 'Emissions trajectory, targets, decarbonization initiatives',
      icon: Thermometer,
      color: 'red',
      bgClass: 'bg-red-100',
      textClass: 'text-red-600',
      btnClass: 'bg-red-600 hover:bg-red-700',
      pages: '10-12 pages'
    },
    {
      id: 'stakeholder',
      name: 'Stakeholder Impact Report',
      description: 'Employee, community, customer, investor impact metrics',
      icon: Users,
      color: 'pink',
      bgClass: 'bg-pink-100',
      textClass: 'text-pink-600',
      btnClass: 'bg-pink-600 hover:bg-pink-700',
      pages: '6-8 pages'
    }
  ];

  // Get current industry data
  const currentIndustry = industries.find(i => i.id === selectedIndustry);
  const currentSize = companySizes.find(s => s.id === companySize);
  const currentMarket = markets.find(m => m.id === market);

  // ESRS Coverage Data
  const esrsCoverage = [
    { standard: 'ESRS 2 (General)', coverage: 95, status: 'complete' },
    { standard: 'E1 (Climate)', coverage: 88, status: 'complete' },
    { standard: 'E2 (Pollution)', coverage: 75, status: 'in-progress' },
    { standard: 'E3 (Water)', coverage: 70, status: 'in-progress' },
    { standard: 'E4 (Biodiversity)', coverage: 60, status: 'in-progress' },
    { standard: 'E5 (Circular)', coverage: 65, status: 'in-progress' },
    { standard: 'S1 (Workforce)', coverage: 90, status: 'complete' },
    { standard: 'S2 (Value Chain)', coverage: 72, status: 'in-progress' },
    { standard: 'S3 (Communities)', coverage: 68, status: 'in-progress' },
    { standard: 'S4 (Consumers)', coverage: 78, status: 'in-progress' },
    { standard: 'G1 (Governance)', coverage: 92, status: 'complete' }
  ];

  // Materiality Matrix Data
  const materialityData = [
    { topic: 'Climate Change', financial: 9, impact: 9, priority: 'high' },
    { topic: 'Water Stress', financial: 6, impact: 8, priority: 'high' },
    { topic: 'Biodiversity', financial: 5, impact: 7, priority: 'medium' },
    { topic: 'Employee Safety', financial: 7, impact: 9, priority: 'high' },
    { topic: 'Supply Chain', financial: 8, impact: 8, priority: 'high' },
    { topic: 'Data Privacy', financial: 7, impact: 6, priority: 'medium' },
    { topic: 'Board Diversity', financial: 5, impact: 6, priority: 'medium' },
    { topic: 'Community Impact', financial: 4, impact: 7, priority: 'medium' }
  ];

  // Benchmark Data
  const benchmarkData = [
    { metric: 'ESG Score', company: 78, industry: 72, leader: 88 },
    { metric: 'Carbon Intensity', company: 2.3, industry: 3.1, leader: 1.5 },
    { metric: 'Employee Engagement', company: 82, industry: 75, leader: 90 },
    { metric: 'Board Diversity', company: 40, industry: 35, leader: 50 },
    { metric: 'Supply Chain Audits', company: 75, industry: 68, leader: 95 }
  ];

  // Climate Scenarios
  const climateScenarios = [
    { year: 2024, baseline: 4800, scenario_15c: 4800, scenario_2c: 4800, scenario_3c: 4800 },
    { year: 2026, baseline: 4600, scenario_15c: 3850, scenario_2c: 4200, scenario_3c: 4500 },
    { year: 2028, baseline: 4400, scenario_15c: 3100, scenario_2c: 3700, scenario_3c: 4300 },
    { year: 2030, baseline: 4200, scenario_15c: 2400, scenario_2c: 3200, scenario_3c: 4200 },
    { year: 2035, baseline: 3800, scenario_15c: 1200, scenario_2c: 2200, scenario_3c: 4000 },
    { year: 2040, baseline: 3500, scenario_15c: 480, scenario_2c: 1400, scenario_3c: 3900 },
    { year: 2050, baseline: 3000, scenario_15c: 0, scenario_2c: 600, scenario_3c: 3800 }
  ];

  // Generate Report Handler
  const generateReport = async (reportType) => {
    setGeneratingReport(true);
    setSelectedReportType(reportType);
    
    try {
      // Simulate AI report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In production, call Claude API to generate narrative
      alert(`${reportType.name} generated successfully!\n\nIndustry: ${currentIndustry.name}\nSize: ${currentSize.name}\nMarket: ${currentMarket.name}\n\nReport would be downloaded as PDF.`);
    } catch (error) {
      console.error('Report generation failed:', error);
    } finally {
      setGeneratingReport(false);
      setSelectedReportType(null);
    }
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 size={36} />
            <div>
              <h1 className="text-3xl font-bold">Analytics Hub</h1>
              <p className="text-indigo-100">Industry-specific insights with holistic report generation</p>
            </div>
          </div>
          <div className="flex gap-3">
            <select 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 rounded-lg bg-white text-slate-900 font-semibold"
            >
              <option value="1m">Last Month</option>
              <option value="3m">Last 3 Months</option>
              <option value="6m">Last 6 Months</option>
              <option value="12m">Last 12 Months</option>
              <option value="ytd">Year to Date</option>
            </select>
            <button className="px-4 py-2 bg-white text-indigo-700 rounded-lg font-semibold hover:bg-indigo-50 flex items-center gap-2">
              <Download size={18} />
              Export Data
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Industry Selector */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Factory size={20} className="text-blue-600" />
            Industry
          </h3>
          <div className="space-y-2">
            {industries.map(industry => {
              const Icon = industry.icon;
              return (
                <button
                  key={industry.id}
                  onClick={() => setSelectedIndustry(industry.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                    selectedIndustry === industry.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} className={selectedIndustry === industry.id ? 'text-blue-600' : 'text-slate-600'} />
                    <span className={`font-semibold ${selectedIndustry === industry.id ? 'text-blue-900' : 'text-slate-700'}`}>
                      {industry.name}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Company Size */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 size={20} className="text-purple-600" />
            Company Size
          </h3>
          <div className="space-y-3">
            {companySizes.map(size => (
              <button
                key={size.id}
                onClick={() => setCompanySize(size.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  companySize === size.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className="font-bold text-slate-900 mb-1">{size.name}</div>
                <div className="text-sm text-slate-600">{size.description}</div>
                <div className="text-xs text-purple-600 mt-2">{size.esrsRequirements}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Market Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Globe size={20} className="text-green-600" />
            Market
          </h3>
          <div className="space-y-3">
            {markets.map(mkt => (
              <button
                key={mkt.id}
                onClick={() => setMarket(mkt.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  market === mkt.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-slate-900">{mkt.name}</span>
                  {mkt.mandatory && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                      MANDATORY
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-600">
                  {mkt.frameworks.join(', ')}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Current Configuration Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Current Configuration</h3>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Factory size={16} className="text-blue-600" />
                <span className="font-semibold text-slate-700">Industry:</span>
                <span className="text-slate-900">{currentIndustry.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-purple-600" />
                <span className="font-semibold text-slate-700">Size:</span>
                <span className="text-slate-900">{currentSize.name} ({currentSize.description})</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-green-600" />
                <span className="font-semibold text-slate-700">Market:</span>
                <span className="text-slate-900">{currentMarket.name}</span>
              </div>
            </div>
            <div className="mt-3 text-sm text-slate-600">
              <span className="font-semibold">Material Topics:</span> {currentIndustry.materialTopics.join(', ')}
            </div>
          </div>
        </div>
      </div>

      {/* Data Source Indicator */}
      {!usingRealData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-yellow-600" />
          <div>
            <p className="text-sm font-semibold text-yellow-900">Demo Analytics Mode</p>
            <p className="text-xs text-yellow-700">
              Upload data via <a href="/data-upload" className="underline font-semibold">Data Upload</a> to see real analytics calculated from your data.
            </p>
          </div>
        </div>
      )}
      {usingRealData && analyticsData && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle size={20} className="text-green-600" />
          <div>
            <p className="text-sm font-semibold text-green-900">Real Data Analytics</p>
            <p className="text-xs text-green-700">
              Analyzing {analyticsData.total_uploads} uploaded files across {Object.keys(analyticsData.data_by_type).length} data categories. 
              Last update: {analyticsData.latest_upload ? new Date(analyticsData.latest_upload).toLocaleString() : 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Industry-Specific KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Environmental</h3>
            <Leaf className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-slate-900">{currentIndustry.kpis.environmental}</p>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-3">
            <div className="bg-green-500 h-2 rounded-full" style={{width: `${currentIndustry.kpis.environmental}%`}}></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Social</h3>
            <Users className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-slate-900">{currentIndustry.kpis.social}</p>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-3">
            <div className="bg-blue-500 h-2 rounded-full" style={{width: `${currentIndustry.kpis.social}%`}}></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Governance</h3>
            <Shield className="text-purple-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-slate-900">{currentIndustry.kpis.governance}</p>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-3">
            <div className="bg-purple-500 h-2 rounded-full" style={{width: `${currentIndustry.kpis.governance}%`}}></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Financial</h3>
            <DollarSign className="text-orange-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-slate-900">{currentIndustry.kpis.financial}</p>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-3">
            <div className="bg-orange-500 h-2 rounded-full" style={{width: `${currentIndustry.kpis.financial}%`}}></div>
          </div>
        </div>
      </div>

      {/* ESRS Coverage Tracker */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <CheckCircle size={20} className="text-green-600" />
          ESRS Disclosure Coverage Tracker
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {esrsCoverage.map((item, idx) => (
            <div key={idx} className="border-l-4 bg-slate-50 rounded p-4" style={{
              borderLeftColor: item.status === 'complete' ? '#10b981' : '#f59e0b'
            }}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-slate-900">{item.standard}</span>
                <span className="text-2xl font-bold text-slate-900">{item.coverage}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full ${item.status === 'complete' ? 'bg-green-500' : 'bg-orange-500'}`}
                  style={{width: `${item.coverage}%`}}
                ></div>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${
                item.status === 'complete' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-orange-100 text-orange-700'
              }`}>
                {item.status === 'complete' ? 'Complete' : 'In Progress'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Materiality Matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Target size={20} className="text-red-600" />
            Double Materiality Matrix
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                dataKey="financial" 
                name="Financial Materiality" 
                domain={[0, 10]}
                label={{ value: 'Financial Materiality →', position: 'bottom' }}
              />
              <YAxis 
                type="number" 
                dataKey="impact" 
                name="Impact Materiality" 
                domain={[0, 10]}
                label={{ value: 'Impact Materiality →', angle: -90, position: 'left' }}
              />
              <ZAxis range={[100, 400]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-3 border border-slate-300 rounded shadow-lg">
                      <p className="font-bold text-slate-900">{payload[0].payload.topic}</p>
                      <p className="text-sm text-slate-600">Financial: {payload[0].payload.financial}/10</p>
                      <p className="text-sm text-slate-600">Impact: {payload[0].payload.impact}/10</p>
                      <p className="text-xs font-semibold text-red-600 mt-1">
                        {payload[0].payload.priority.toUpperCase()} PRIORITY
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Scatter name="Material Topics" data={materialityData} fill="#8b5cf6">
                {materialityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={
                    entry.priority === 'high' ? '#ef4444' : 
                    entry.priority === 'medium' ? '#f59e0b' : '#10b981'
                  } />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="mt-3 flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>High Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span>Medium Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Low Priority</span>
            </div>
          </div>
        </div>

        {/* Industry Benchmark */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-blue-600" />
            Industry Benchmark Comparison
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={benchmarkData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="metric" angle={-15} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="company" fill="#8b5cf6" name="Your Company" />
              <Bar dataKey="industry" fill="#3b82f6" name="Industry Average" />
              <Bar dataKey="leader" fill="#10b981" name="Industry Leader" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Climate Scenario Planning */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Thermometer size={20} className="text-red-600" />
          Climate Scenario Analysis (Net Zero Pathways)
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={climateScenarios}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis label={{ value: 'Emissions (tCO₂e)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="baseline" stroke="#94a3b8" strokeWidth={2} name="Baseline (No Action)" strokeDasharray="5 5" />
            <Line type="monotone" dataKey="scenario_15c" stroke="#10b981" strokeWidth={3} name="1.5°C Scenario (Net Zero)" />
            <Line type="monotone" dataKey="scenario_2c" stroke="#f59e0b" strokeWidth={2} name="2°C Scenario" />
            <Line type="monotone" dataKey="scenario_3c" stroke="#ef4444" strokeWidth={2} name="3°C+ Scenario" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-green-50 rounded border border-green-200">
            <div className="font-bold text-green-900">1.5°C Pathway</div>
            <div className="text-xs text-green-700 mt-1">Net Zero by 2050 - Paris Aligned</div>
            <div className="text-xs text-green-600 mt-2">Required: 45% reduction by 2030</div>
          </div>
          <div className="p-3 bg-orange-50 rounded border border-orange-200">
            <div className="font-bold text-orange-900">2°C Pathway</div>
            <div className="text-xs text-orange-700 mt-1">Carbon Neutral by 2050</div>
            <div className="text-xs text-orange-600 mt-2">Required: 30% reduction by 2030</div>
          </div>
          <div className="p-3 bg-red-50 rounded border border-red-200">
            <div className="font-bold text-red-900">3°C+ Pathway</div>
            <div className="text-xs text-red-700 mt-1">Business as Usual</div>
            <div className="text-xs text-red-600 mt-2">High climate risk exposure</div>
          </div>
        </div>
      </div>

      {/* Report Generator */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Brain size={20} className="text-purple-600" />
          AI-Powered Report Generator
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {reportTypes.map(report => {
            const Icon = report.icon;
            return (
              <div key={report.id} className="border-2 border-slate-200 rounded-lg p-4 hover:border-purple-400 transition-all">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-3 ${report.bgClass} rounded-lg`}>
                    <Icon size={24} className={report.textClass} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-900 mb-1">{report.name}</h4>
                    <p className="text-xs text-slate-600 mb-2">{report.description}</p>
                    <span className="text-xs text-slate-500">{report.pages}</span>
                  </div>
                </div>
                <button
                  onClick={() => generateReport(report)}
                  disabled={generatingReport}
                  className={`w-full px-4 py-2 ${report.btnClass} text-white rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2`}
                >
                  {generatingReport && selectedReportType?.id === report.id ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Generate Report
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Brain size={20} className="text-purple-600 mt-1" />
            <div>
              <h4 className="font-bold text-purple-900 mb-1">AI-Generated Narratives</h4>
              <p className="text-sm text-purple-700">
                Reports are automatically generated using Claude AI based on your configuration ({currentIndustry.name}, {currentSize.name}, {currentMarket.name}). 
                Narratives are ESRS-compliant and include industry-specific insights, benchmarks, and recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
